/**
 * The View as read-only contract, client side.
 *
 * Mirror of the worker's chokepoint (api/src/index.ts, VIEW_AS_HEADER /
 * VIEW_AS_SAFE_METHODS / the two write carve-outs). The worker is the guarantee;
 * this refuses the same requests before they leave the browser, now that the
 * lens renders the target's own buttons. It keys off the view-as HEADER, not
 * the lens state, exactly like the worker: a request sent with the real
 * operator's headers (the picker's own reads) is not a lensed request.
 *
 * The carve-outs pass through to the worker, which still checks the REAL
 * operator's edit.zones / edit.rates before letting them write.
 */
export const VIEW_AS_HEADER = 'X-Parkify-View-As'
export const VIEW_AS_BLOCKED_MESSAGE =
  'Read-only in View as. Exit the view to make changes.'

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

function carvedOut(method: string, pathname: string): boolean {
  if (pathname === '/api/zones') return method === 'POST' || method === 'DELETE'
  if (pathname === '/api/rates' || pathname === '/api/rates/preview') return method === 'POST'
  // An Avery turn is a POST but not a write: it reads and returns proposals,
  // and the worker runs it as the target without storing them (index.ts
  // isViewAsAveryChat). Executing a proposal still goes through blocked routes.
  if (pathname === '/api/avery/chat') return method === 'POST'
  return false
}

function headerValue(headers: HeadersInit | undefined, name: string): string | null {
  if (!headers) return null
  if (headers instanceof Headers) return headers.get(name)
  if (Array.isArray(headers)) {
    const hit = headers.find(([k]) => k.toLowerCase() === name.toLowerCase())
    return hit ? hit[1] : null
  }
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() === name.toLowerCase()) return v
  }
  return null
}

export function isViewAsBlockedRequest(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  apiBase: string,
): boolean {
  const req = input instanceof Request ? input : null
  const target = (init?.headers !== undefined
    ? headerValue(init.headers, VIEW_AS_HEADER)
    : req?.headers.get(VIEW_AS_HEADER)) ?? ''
  if (!target.trim()) return false
  const method = (init?.method ?? req?.method ?? 'GET').toUpperCase()
  if (SAFE_METHODS.has(method)) return false
  const href = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
  let pathname: string
  try {
    pathname = new URL(href, apiBase).pathname
  } catch {
    return true
  }
  return !carvedOut(method, pathname)
}
